import { useState } from "react";
import { Input } from "./ui/input";
import { Plus, X, ListChecks, Type, Upload, ToggleLeft } from "lucide-react";

export const QUESTION_TEMPLATES = [
  { type: "yes_no", label: "Can you follow the script exactly?" },
  { type: "yes_no", label: "Can you deliver this in the next 7 days?" },
  { type: "yes_no", label: "Are you happy for us to run this as a paid ad?" },
  { type: "yes_no", label: "Do you have the product / can you source it locally?" },
  { type: "upload", label: "Upload a video or portfolio" },
  { type: "text", label: "What's your idea for the hook / first 3 seconds?" },
  { type: "text", label: "Which of your past videos is closest to this brief?" },
];

const TYPE_META = {
  yes_no: { label: "Yes / No", icon: ToggleLeft },
  text: { label: "Short answer", icon: Type },
  upload: { label: "Upload or link", icon: Upload },
};

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export function CampaignQuestionsEditor({ questions, onChange }) {
  const [custom, setCustom] = useState("");
  const [customType, setCustomType] = useState("yes_no");
  const has = (label) => questions.some((q) => q.label === label);
  const add = (label, type) => {
    if (!label.trim() || has(label.trim()) || questions.length >= 10) return;
    onChange([...questions, { id: newId(), type, label: label.trim() }]);
  };
  const remove = (id) => onChange(questions.filter((q) => q.id !== id));

  return (
    <div className="space-y-3" data-testid="campaign-questions-editor">
      <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2"><ListChecks className="h-3.5 w-3.5" /> Screening questions <span className="text-zinc-600 normal-case tracking-normal">· optional</span></div>
      <p className="text-[11px] text-zinc-500">Creators must answer these when they apply — makes shortlisting much faster. Tap a template or write your own.</p>
      {questions.length > 0 && (
        <ol className="space-y-2" data-testid="campaign-questions-list">
          {questions.map((q, i) => {
            const M = TYPE_META[q.type] || TYPE_META.yes_no;
            return (
              <li key={q.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] hairline" data-testid={`campaign-question-${i}`}>
                <span className="text-[10px] font-mono text-zinc-500 w-4">{i + 1}</span>
                <span className="flex-1 text-sm text-white">{q.label}</span>
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400"><M.icon className="h-3 w-3" /> {M.label}</span>
                <button type="button" onClick={() => remove(q.id)} className="text-zinc-500 hover:text-nexus-pink" data-testid={`campaign-question-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
              </li>
            );
          })}
        </ol>
      )}
      <div className="flex flex-wrap gap-2">
        {QUESTION_TEMPLATES.filter((t) => !has(t.label)).map((t) => {
          const M = TYPE_META[t.type];
          return (
            <button key={t.label} type="button" onClick={() => add(t.label, t.type)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-white/10 text-zinc-300 hover:border-nexus-purple hover:text-white transition-colors" data-testid={`question-template-${t.type}`}>
              <Plus className="h-3 w-3" /> {t.label} <M.icon className="h-3 w-3 text-zinc-500" />
            </button>
          );
        })}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Input value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add(custom, customType), setCustom(""))} placeholder="Write your own question…" className="flex-1 min-w-[200px] bg-white/[0.02] border-white/10 text-white" data-testid="question-custom-input" />
        <select value={customType} onChange={(e) => setCustomType(e.target.value)} className="h-10 rounded-md bg-[#0C0C10] border border-white/10 text-white text-sm px-2" data-testid="question-custom-type">
          {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button type="button" onClick={() => { add(custom, customType); setCustom(""); }} disabled={!custom.trim()} className="h-10 px-4 rounded-full nexus-gradient text-white text-sm font-semibold disabled:opacity-40" data-testid="question-custom-add">Add</button>
      </div>
    </div>
  );
}
